/**
 * Plain Language Insight
 * One-line performance insight in plain English
 * Serves RM (client communication) and provides context for AM/IA
 */

import React from 'react';
import { Lightbulb, CheckCircle, AlertCircle } from 'lucide-react';

interface PlainLanguageInsightProps {
  primaryDriver: string; // e.g., "technology sector"
  driverContribution: number; // e.g., 2.1
  riskStatus: 'within-target' | 'above-target' | 'below-target';
  customInsight?: string; // Optional custom message
}

const PlainLanguageInsight: React.FC<PlainLanguageInsightProps> = ({
  primaryDriver,
  driverContribution,
  riskStatus,
  customInsight
}) => {
  const getRiskStatusText = () => {
    switch (riskStatus) {
      case 'within-target':
        return { text: 'Within target', icon: CheckCircle, color: 'text-green-600' };
      case 'above-target':
        return { text: 'Above target', icon: AlertCircle, color: 'text-yellow-600' };
      case 'below-target':
        return { text: 'Below target', icon: CheckCircle, color: 'text-blue-600' };
    }
  };

  const riskInfo = getRiskStatusText();
  const RiskIcon = riskInfo.icon;

  const driverSign = driverContribution >= 0 ? '+' : '';
  const performanceText = customInsight ||
    `Strong performance driven by ${primaryDriver} (${driverSign}${driverContribution.toFixed(1)}%)`;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-gray-800">
            {performanceText}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-600">Risk level:</span>
            <div className={`flex items-center gap-1 ${riskInfo.color}`}>
              <RiskIcon className="w-4 h-4" />
              <span className="text-xs font-medium">{riskInfo.text}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlainLanguageInsight;
